<template>
  <div class="date-picker">
    <div class="date-picker__header">
      <button
        type="button"
        class="date-picker__change-select-button date-picker__change-select-button--prev"
        :class="{
          'date-picker__change-select-button--disabled': currentYear === minYear && currentMonth === minMonth
        }"
        :disabled="currentYear === minYear && currentMonth === minMonth"
        @click.prevent="prevMonthBtnClickHandler"
      >
				<SVGMidCircleChevronLeft/>
			</button>
      <button
        type="button"
        class="date-picker__change-select-button date-picker__change-select-button--next"
        :class="{
          'date-picker__change-select-button--disabled': currentYear === maxYear && currentMonth === maxMonth
        }"
        :disabled="currentYear === maxYear && currentMonth === maxMonth"
        @click.prevent="nextMonthBtnClickHandler"
      >
				<SVGMidCircleChevronRight/>
			</button>

      <div class="date-picker__select-wrapper date-picker__select-wrapper--month-wrapper">
        <select
          class="date-picker__select"
          v-model="currentMonth"
          @change="monthSelectChangeHandler"
        >
          <option
            v-for="month in 12"
            :key="month"
            :value="month"
            :disabled="(currentYear === maxYear && month > maxMonth) || (currentYear === minYear && month < minMonth)"
          >
            {{ getMonthDisplayName(month) }}
          </option>
        </select>
				<span
          class="date-picker__select-label"
          v-html="currentMonthDisplay"
        />
      </div>
      <div class="date-picker__select-wrapper date-picker__select-wrapper--year-wrapper">
        <select
          class="date-picker__select"
          v-model="currentYear"
          @change="yearSelectChangeHandler"
        >
          <option
            v-for="(year, index) in maxYear - minYear + 1"
            :key="minYear + index"
            :value="minYear + index"
          >
            {{ minYear + index }}
          </option>
        </select>
				<span
          class="date-picker__select-label"
          v-html="currentYear"
        />
      </div>
    </div>
    <div class="date-picker__days-wrapper">
      <span
				class="date-picker__day date-picker__day--name"
				v-for="day in dayNames"
				:key="day"
				v-html="day"
			/>
      <span
        class="date-picker__day date-picker__day--empty"
        v-for="emptyDay in emptyDays"
        :key="emptyDay"
      />
      <label
        class="date-picker__day"
        v-for="day in daysOfMonth"
        :key="`${uniqueId}-day-${currentYear}-${currentMonth}-${day}`"
        :for="`${uniqueId}-day-${currentYear}-${currentMonth}-${day}`"
      >
			<input
          class="date-picker__day-input"
          type="radio"
          v-model="selectedDate"
          :value="formatDayDate(day)"
          :id="`${uniqueId}-day-${currentYear}-${currentMonth}-${day}`"
          :disabled="(currentYear === minYear && currentMonth === minMonth && day < minDay) || (currentYear === maxYear && currentMonth === maxMonth && day > maxDay) || isDateDisabled(new Date(currentYear, currentMonth - 1, day))"
          @change="dayInputChangeHandler"
        >
        <span
          class="date-picker__day-label"
					:class="{
						'date-picker__day-label--today': today === formatDayDate(day)
					}"
          v-on="selectedDate === formatDayDate(day) ? {click: dayInputChangeHandler} : {}"
        >
          {{ day }}
        </span>
      </label>
    </div>
  </div>
</template>

<script>
import { leadingZero } from '@/utils/leadingZero'
export default {
	props: {
		initialSelectedDate: String,
		minDate: {
			type: String,
			default: 'today'
		},
		maxDate: String,
		isDateDisabled: {
			type: Function,
			default: () => false
		},
	},
	data() {
		return {
			uniqueId: `calendar-${(Date.now().toString(36) + Math.random().toString(36).substring(2, 7))}`,
			currentYear: null,
			currentMonth: null,
			selectedYear: null,
			selectedMonth: null,
			selectedDay: null,
			today: (() => {
				const today = new Date()
				return this.formatDayDate(today.getDate(), today.getMonth() + 1, today.getFullYear())
			})(),
			minYear: null,
			minMonth: null,
			minDay: null,
			maxYear: null,
			maxMonth: null,
			maxDay: null,
			dayNames: [
				this.$t('dates.daysShort.monday'),
				this.$t('dates.daysShort.tuesday'),
				this.$t('dates.daysShort.wednesday'),
				this.$t('dates.daysShort.thursday'),
				this.$t('dates.daysShort.friday'),
				this.$t('dates.daysShort.saturday'),
				this.$t('dates.daysShort.sunday'),
			],
			monthNames: [
				this.$t('dates.months.january'),
				this.$t('dates.months.february'),
				this.$t('dates.months.march'),
				this.$t('dates.months.april'),
				this.$t('dates.months.may'),
				this.$t('dates.months.june'),
				this.$t('dates.months.july'),
				this.$t('dates.months.august'),
				this.$t('dates.months.september'),
				this.$t('dates.months.october'),
				this.$t('dates.months.november'),
				this.$t('dates.months.december'),
			],
		};
	},
	computed: {
		daysOfMonth() {
			return this.getNumberOfDaysInMonth(this.currentMonth);
		},
		emptyDays() {
			let dayOfWeek = (new Date(this.currentYear, this.currentMonth-1, 1, 0, 0 ,0,0)).getDay();
			return dayOfWeek === 0 ? 6 : -1 + dayOfWeek;
		},
		currentMonthDisplay() {
			return this.getMonthDisplayName(this.currentMonth);
		},
		selectedDate: {
			get() {
				return this.formatDayDate(this.selectedDay, this.selectedMonth, this.selectedYear)
			},
			set(value) {
				const [day, month, year] = value.split('.')
				this.selectedDay = +day
				this.selectedMonth = +month
				this.selectedYear = +year
			},
		}
	},
	methods: {
		formatDayDate(day, month = this.currentMonth, year = this.currentYear) {
			return [
				leadingZero(day),
				leadingZero(month),
				year,
			].join('.')
		},
		update() {
			this.setInitialMinDate();
			this.setInitialMaxDate();
			this.setInitialSelectedDate();
		},
		setInitialMinDate() {
			let initialMinDate = this.minDate;

			if (['yesterday', 'today', 'tomorrow'].includes(initialMinDate)) {
        let date = new Date();

        if (initialMinDate === 'yesterday') {
          date.setDate(date.getDate() - 1)
        } else if (initialMinDate === 'tomorrow') {
          date.setDate(date.getDate() + 1)
        }

				initialMinDate = `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
			} else if (!initialMinDate) {
				// 3153600000000 is ~100 years in miliseconds
				initialMinDate = `01.01.${(new Date((new Date()).getTime() - 3153600000000)).getFullYear()}`;
			}

			this.setMinDate(initialMinDate);
		},
		setMinDate(date) {
			let dateArray = date.split('.')
				.map(dateElement => {
					return parseInt(dateElement);
				});

			let daysInCurrentMonth;

			this.minYear = dateArray[2];
			this.minMonth = dateArray[1] < 12 ? dateArray[1] : 12;
			daysInCurrentMonth = this.getNumberOfDaysInMonth(this.minMonth);
			this.minDay = dateArray[0] < daysInCurrentMonth ? dateArray[0] : daysInCurrentMonth;
		},
		setInitialMaxDate() {
			let initialMaxDate = this.maxDate;

			if (['yesterday', 'today', 'tomorrow'].includes(initialMaxDate)) {
        let date = new Date();

        if (initialMaxDate === 'yesterday') {
          date.setDate(date.getDate() - 1)
        } else if (initialMaxDate === 'tomorrow') {
          date.setDate(date.getDate() + 1)
        }

				initialMaxDate = `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
			} else if (!initialMaxDate) {
				// 315360000000 is ~10 years in miliseconds
				initialMaxDate = `31.12.${(new Date((new Date()).getTime() + 315360000000)).getFullYear()}`;
			}

			this.setMaxDate(initialMaxDate);
		},
		setMaxDate(date) {
			let dateArray = date.split('.')
				.map(dateElement => {
					return parseInt(dateElement);
				});

			let daysInCurrentMonth;

			this.maxYear = dateArray[2] > this.minYear ? dateArray[2] : this.minYear;

			if (this.minYear === this.maxYear) {
				this.maxMonth = dateArray[1] > this.minMonth ? (dateArray[1] < 12 ? dateArray[1] : 12) : this.minMonth;
			} else {
				this.maxMonth = dateArray[1] < 12 ? dateArray[1] : 12;
			}

			daysInCurrentMonth = this.getNumberOfDaysInMonth(this.maxMonth);

			if (this.minYear === this.maxYear && this.minMonth === this.maxMonth) {
				this.maxDay = dateArray[0] > this.minDay ? (dateArray[0] < daysInCurrentMonth ? dateArray[0] : daysInCurrentMonth) : this.minDay;
			} else {
				this.maxDay = dateArray[0] < daysInCurrentMonth ? dateArray[0] : daysInCurrentMonth;
			}
		},
		setInitialSelectedDate() {
			let initialDate = this.initialSelectedDate;

			if (initialDate) {
				this.setSelectedDate(initialDate);
			} else {
				let today = new Date();
				initialDate = `${today.getDate()}.${today.getMonth() + 1}.${today.getFullYear()}`;
				this.setSelectedYear(today.getFullYear())
				this.setSelectedMonth(today.getMonth() + 1)
			}
		},
		setSelectedDate(date) {
			let dateArray = date.split('.')
				.map(dateElement => {
					return parseInt(dateElement);
				});

			this.setSelectedYear(dateArray[2]);
			this.setSelectedMonth(dateArray[1]);
			this.setSelectedDay(dateArray[0]);
		},
		setSelectedYear(year) {
			this.selectedYear = Math.min(Math.max(year, this.minYear), this.maxYear)
			this.currentYear = this.selectedYear
		},
		setSelectedMonth(month) {
			if (this.selectedYear === this.maxYear && this.selectedYear === this.minYear) {
        this.selectedMonth = Math.min(Math.max(month, this.minMonth), this.maxMonth)
			} else if (this.selectedYear === this.maxYear) {
				this.selectedMonth = Math.min(Math.max(month, 1), this.maxMonth)
			} else if (this.selectedYear === this.minYear) {
				this.selectedMonth = Math.min(Math.max(month, this.minMonth), 12)
			} else {
				this.selectedMonth = Math.min(Math.max(month, 1), 12)
			}
			this.currentMonth = this.selectedMonth
		},
		setSelectedDay(day) {
			let daysInSelectedMonth = this.getNumberOfDaysInMonth(this.selectedMonth);

			if (this.selectedYear === this.maxYear && this.selectedYear === this.minYear) {
        if (this.selectedMonth === this.maxMonth && this.selectedMonth === this.minMonth) {
          this.selectedDay = Math.min(Math.max(day, this.minDay), this.maxDay)
				} else if (this.selectedMonth === this.maxMonth) {
          this.selectedDay = Math.min(Math.max(day, 1), this.maxDay)
				} else if (this.selectedMonth === this.minMonth) {
          this.selectedDay = Math.min(Math.max(day, this.minDay), daysInSelectedMonth)
				} else {
          this.selectedDay = Math.min(Math.max(day, 1), daysInSelectedMonth)
        }
			} else if (this.selectedYear === this.maxYear && this.selectedMonth === this.maxMonth) {
        this.selectedDay = Math.min(Math.max(day, 1), this.maxDay)
			} else if (this.selectedYear === this.minYear && this.selectedMonth === this.minMonth) {
        this.selectedDay = Math.min(Math.max(day, this.minDay), daysInSelectedMonth)
			} else {
				this.selectedDay = Math.min(Math.max(day, 1), daysInSelectedMonth)
			}
		},
		getNumberOfDaysInMonth(month) {
			if ([1, 3, 5, 7, 8, 10, 12].includes(month)) {
				return 31;
			} else {
				return this.currentMonth === 2 ? (this.currentYear % 4 === 0 ? 29 : 28) : 30;
			}
		},
		yearSelectChangeHandler() {
			if (this.currentYear === this.minYear && this.currentMonth < this.minMonth) {
				this.currentMonth = this.minMonth;
			}

			if (this.currentYear === this.maxYear && this.currentMonth > this.maxMonth) {
				this.currentMonth = this.maxMonth;
			}
		},
		monthSelectChangeHandler() {
		},
		prevYearBtnClickHandler() {
			if (this.currentYear !== this.minYear) {
				this.currentYear = this.currentYear - 1;
				this.yearSelectChangeHandler();
			}
		},
		nextYearBtnClickHandler() {
			if (this.currentYear !== this.maxYear) {
				this.currentYear = this.currentYear + 1;
				this.yearSelectChangeHandler();
			}
		},
		prevMonthBtnClickHandler() {
			if (!(this.currentYear === this.minYear && this.currentMonth === this.minMonth)) {
				if (this.currentMonth === 1) {
					this.currentMonth = 12;
					this.currentYear = this.currentYear - 1;
					this.monthSelectChangeHandler();
					this.yearSelectChangeHandler();
				} else {
					this.currentMonth = this.currentMonth - 1;
					this.monthSelectChangeHandler();
				}
			}
		},
		nextMonthBtnClickHandler() {
			if (!(this.currentYear === this.maxYear && this.currentMonth === this.maxMonth)) {
				if (this.currentMonth === 12) {
					this.currentMonth = 1;
					this.currentYear = this.currentYear + 1;
					this.monthSelectChangeHandler();
					this.yearSelectChangeHandler();
				} else {
					this.currentMonth = this.currentMonth + 1;
					this.monthSelectChangeHandler();
				}
			}
		},
		dayInputChangeHandler() {
			this.$emit('change', {
				value: this.selectedDate,
				// day: this.selectedDay,
				// month: this.selectedMonth,
				// year: this.selectedYear
			});
		},
		getMonthDisplayName(monthNr) {
			return this.monthNames[monthNr - 1];
		}
	},
	created() {
		this.setInitialMinDate();
		this.setInitialMaxDate();
		this.setInitialSelectedDate();
	}
};
</script>
